// invest_FE/src/services/sessionService.js
import { apiFetch } from '../utils/apiClient';
import { BASE_URL } from '../utils/config';

/**
 * Pings the server to keep the user's session marked as active.
 * This should be called periodically by all users (guests and logged-in).
 * @param {string} sessionId - A unique identifier for the browser tab/session.
 * @param {string|null} token - The user's auth token, or null if guest.
 * @returns {Promise<Response>} The raw response from the fetch call.
 */
export const pingServer = async (sessionId, token) => {
  // The apiFetch utility handles adding the Authorization header if the token exists.
  // We explicitly pass the token here to ensure it's always included for logged-in users.
  await apiFetch(
    `${BASE_URL}/api/sessions/ping`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sessionId }),
    },
    token
  );
};

/**
 * Fetches the current count of active users. This is an admin-only endpoint.
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<{total: number, registered: number, guests: number}>} The active user counts.
 */
export const getActiveUserCount = async (token) => {
  const response = await apiFetch(`${BASE_URL}/api/sessions/count`, {}, token);
  if (!response.ok) {
    throw new Error('Failed to fetch active user count');
  }
  return response.json();
};

/**
 * Fetches a detailed list of active users from the backend.
 * This is an admin-only function.
 * @param {string} token - The admin user's JWT.
 * @returns {Promise<Array>} A promise that resolves to an array of active user details.
 */
export const getActiveUsersDetails = async (token) => {
  const response = await apiFetch(`${BASE_URL}/api/sessions/details`, {}, token);
  if (!response.ok) {
    throw new Error('Failed to fetch active user details');
  }
  return response.json();
};

export const getSessionId = () => {
  let sessionId = sessionStorage.getItem('appSessionId');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('appSessionId', sessionId);
  }
  return sessionId;
};
